import * as React from 'react'
import {Box, Typography} from '@mui/material'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, ERROR, WARNING, SUCCESS, withOpacity} from '../../../../styles/foundation/colors'
import {int} from '../format'
import {gateState} from './rows'

/**
 * Weight Freeze (v2) — Initiate: the per-row DWS gate list shown inside the
 * raise drawer's "DWS data on file" section.
 *
 * One line per row the request covers, coloured by the SAME merchant-first
 * three-way state the submit gate itself reads (`gateState` in ./rows) —
 * never the DWS rollup's own none/partial/both, which treats sorter and
 * merchant symmetrically. A row the courier has scanned a hundred times but
 * the merchant has never filmed reads 'none' here, exactly as the gate
 * treats it, because from the freeze's point of view there is nothing of
 * the merchant's own to argue from.
 *
 * Presentational only — no unit tests. `gateState` is imported, never
 * recomputed, and this component does no partitioning of its own.
 */
const STATE_META = {
  none: {color: ERROR, Icon: ImageNotSupportedOutlinedIcon},
  partial: {color: WARNING, Icon: WarningAmberOutlinedIcon},
  both: {color: SUCCESS, Icon: VerifiedUserOutlinedIcon},
}

const countSx = (zero) => ({
  fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
  color: zero ? ERROR : NAVY, fontVariantNumeric: 'tabular-nums',
})

/**
 * @param {{rows: Array<{row: object, ev: object}>}} props — the `rows`
 * half of `dwsPartition()` from ./request. Never recomputed here.
 */
export default function IniGateList({rows}) {
  return (
    <Box
      component='ul'
      aria-label='DWS evidence on file for each row this request covers'
      sx={{listStyle: 'none', m: 0, p: 0, border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden'}}
    >
      {rows.map(({row, ev}, i) => {
        const state = gateState(ev)
        const {color, Icon} = STATE_META[state]
        return (
          <Box
            component='li'
            key={row.key}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
              borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
              bgcolor: withOpacity(color, 0.06),
            }}
          >
            <Icon sx={{fontSize: 15, color, flexShrink: 0}} aria-hidden='true' />
            <Typography
              title={row.label}
              sx={{
                fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, flex: '1 1 auto',
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {row.label}
            </Typography>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, whiteSpace: 'nowrap'}}>
              Sorter <Box component='span' sx={countSx(!ev.sorter)}>{ev.sorter ? int(ev.sorter) : 'none'}</Box>
            </Typography>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, whiteSpace: 'nowrap'}}>
              Yours <Box component='span' sx={countSx(!ev.merchant)}>{ev.merchant ? int(ev.merchant) : 'none'}</Box>
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}
